import {
  DefaultEventHandlers,
  EventTypes,
  getNodesFromSelector,
  HISTORY_ACTION_MODE,
  Node,
  NodeId,
  NodeInfo,
  NodeSelector,
} from '@candulabs/craft';
import cloneDeep from 'lodash/cloneDeep';
import throttle from 'lodash/throttle';
import { RootStateOrAny } from 'react-redux';

import { getDOMInfo } from 'craft/components/Indicator/getDomInfo';
import { getDropActionFromNodePosition } from 'craft/components/Indicator/getDropActionFromNodePosition';
import { isRootNode } from 'craft/constants';
import {
  activeEditableAncestor,
  createFocusTupleFromTargetElement,
  getRTEAncestorFromTargetElement,
} from 'craft/rte/utils';
import { selectCollectionItemEvents, setContextMenu } from 'craft/state';
import { setCollectionItemEvents } from 'craft/state/collections';
import { CanduCraftIndicator } from 'craft/types';
import { shouldCraftIgnoreEvent } from 'craft/utils';
import { getAllDomNodesFromDocument, getDomNodeFromDocument } from 'craft/utils/viewport';

import { createShadow } from './createShadow';

export class EventHandlers extends DefaultEventHandlers {
  reduxStore: RootStateOrAny;

  clickedRTE: Element | null;

  constructor(store: any, reduxStore: RootStateOrAny) {
    super({
      store,
      /**
       * Ideally, we would only want the shiftKey as the multi-select hotkey.
       * However, on Chromium, dragging doesn't work perfect when shift is pressed (https://bugs.chromium.org/p/chromium/issues/detail?id=982219)
       * Hence, for the time being, let's use the metaKey and altKey as a fallback
       */
      isMultiSelectEnabled: (e: MouseEvent) => !!e.metaKey || !!e.altKey || !!e.shiftKey,
    });

    this.reduxStore = reduxStore;
    this.clickedRTE = null;
  }

  handlers() {
    const parentHandlers = super.handlers();

    const { store } = this.options;

    return {
      ...parentHandlers,

      hover: (el: HTMLElement, nodeId: NodeId) => {
        const unbindOnMouseOver = this.addCraftEventListener(el, 'mouseover', (e) => {
          e.craft.stopPropagation();
          store.actions.setNodeEvent(EventTypes.hovered, [nodeId]);
        });

        return () => {
          // @ts-expect-error
          store.actions.setNodeEvent(EventTypes.hovered, null);
          unbindOnMouseOver();
        };
      },

      select: (el: HTMLElement, nodeId: NodeId) => {
        let mouseDownTimeOut: NodeJS.Timeout;
        let delay = 200;

        const unbindOnMouseDown = this.addCraftEventListener(el, 'mousedown', (e) => {
          e.craft.stopPropagation();

          clearTimeout(mouseDownTimeOut);

          if (shouldCraftIgnoreEvent(e) || e.button !== 0) {
            return;
          }

          const rteNodeAncestor = getRTEAncestorFromTargetElement(e.target as Element | null);
          if (rteNodeAncestor) {
            if (e.detail === 1) {
              // on first click, keep track of the RTE node and set a delay
              // on click handler to listen for second click
              this.clickedRTE = rteNodeAncestor;
              delay = 300;
            } else {
              if (e.detail === 2 && rteNodeAncestor.isEqualNode(this.clickedRTE)) {
                // same RTE node was clicked twice, let the double click event handle it
                return;
              }

              // a different RTE node was targeted on subsequent clicks, treat like new single RTE click without delay
              this.clickedRTE = null;
              delay = 0;
            }
          } else {
            // if not an RTE node, treat like normal click
            this.clickedRTE = null;
            delay = 0;
          }

          mouseDownTimeOut = setTimeout(() => {
            const { query } = store;
            const focused = query.getEvent(EventTypes.focused).all();

            // if we are in focus mode
            if (focused) {
              const hasActiveEditableAncestor = activeEditableAncestor(e.target as Element | null);
              // remove the focus if we target a node outside the slate editor
              if (!hasActiveEditableAncestor) {
                store.actions.setNodeEvent(EventTypes.focused, []);
              } else {
                // break
                return;
              }
            }

            let newSelectedElementIds: string[] = [];
            const collectionItemId = el?.dataset.collectionItemId;

            this.reduxStore.dispatch(
              setCollectionItemEvents(EventTypes.selected, collectionItemId),
            );

            if (nodeId) {
              const selectedElementIds = query.getEvent(EventTypes.selected).all();
              const isMultiSelect = this.options.isMultiSelectEnabled(e);

              /**
               * Retain the previously select elements if the multi-select condition is enabled
               * or if the currentNode is already selected
               *
               * so users can just click to drag the selected elements around without holding the multi-select key
               */

              if (isMultiSelect || selectedElementIds.includes(nodeId)) {
                newSelectedElementIds = selectedElementIds.filter((selectedId) => {
                  const descendants = query.node(selectedId).descendants(true);
                  const ancestors = query.node(selectedId).ancestors(true);

                  // Deselect ancestors/descendants
                  if (descendants.includes(nodeId) || ancestors.includes(nodeId)) {
                    return false;
                  }

                  return true;
                });
              }

              if (!newSelectedElementIds.includes(nodeId)) {
                newSelectedElementIds.push(nodeId);
              } else {
                newSelectedElementIds.splice(newSelectedElementIds.indexOf(nodeId), 1);
              }
            }

            // make sure dragged over isnt there anymore...
            store.actions.setNodeEvent(EventTypes.draggedOver, []);
            store.actions.setNodeEvent(EventTypes.selected, newSelectedElementIds);
          }, delay);
        });

        const unbindContextMenu = this.addCraftEventListener(el, 'contextmenu', (e) => {
          const hasActiveEditableAncestor = !!activeEditableAncestor(e.target as Element | null);

          // don't open the context menu on the root node
          if (shouldCraftIgnoreEvent(e) || isRootNode(nodeId) || hasActiveEditableAncestor) {
            return;
          }
          e.stopImmediatePropagation();
          e.craft.stopPropagation();
          e.preventDefault();
          this.reduxStore.dispatch(
            setContextMenu({
              clientX: e.clientX,
              clientY: e.clientY,
            }),
          );
        });

        const unbindDbClick = this.addCraftEventListener(el, 'dblclick', (e) => {
          clearTimeout(mouseDownTimeOut);

          const editorOptions = store.query.getOptions();
          if (editorOptions.mode === 'readonly') {
            return;
          }
          if (shouldCraftIgnoreEvent(e)) {
            return;
          }

          const rteNodeAncestor = getRTEAncestorFromTargetElement(e.target as Element | null);
          const hasActiveEditableAncestor = !!activeEditableAncestor(e.target as Element | null);

          if (rteNodeAncestor && !rteNodeAncestor.isEqualNode(this.clickedRTE)) {
            return;
          }

          // we double click on another portion of the same editable node
          if (hasActiveEditableAncestor) {
            e.stopImmediatePropagation();
            e.craft.stopPropagation();
            return;
          }

          // we target a non-rte node, make sure we clean the focused state before propagating
          if (!rteNodeAncestor) {
            const { query } = store;
            const selectedElementIds = query.getEvent(EventTypes.focused).all();
            if (selectedElementIds) {
              store.actions.setNodeEvent(EventTypes.focused, []);
            }
            return;
          }

          // else we target an editable node that is currently not in edit/focus mode
          // we have to stop the propagation immediately, otherwise it will trigger many more re-renders than needed.
          e.stopImmediatePropagation();
          e.craft.stopPropagation();

          const [focusedElement, targetedNode] = createFocusTupleFromTargetElement(
            e.target as Element | null,
          );
          // we need to store which particular node received the click event, as this is where the user intents to see the caret

          store.actions.setNodeEvent(EventTypes.selected, [focusedElement.split('-')[0]]);
          store.actions.setNodeEvent(EventTypes.focused, [
            focusedElement,
            targetedNode.split('-')[0],
          ]);
        });

        return () => {
          // @ts-expect-error
          store.actions.setNodeEvent(EventTypes.selected, null);
          unbindOnMouseDown();
          unbindDbClick();
          unbindContextMenu();
        };
      },

      drop: (el: HTMLElement, targetId: NodeId) => {
        let mousePosition = { x: 0, y: 0 };

        const displayIndicator = () => {
          const { draggedElement } = DefaultEventHandlers;

          if (!draggedElement) {
            return;
          }

          let node: NodeSelector = draggedElement as NodeSelector;
          if ('rootNodeId' in draggedElement) {
            const nodeTree = draggedElement;
            node = nodeTree.nodes[nodeTree.rootNodeId];
          }

          const indicator = this.getDropPlaceholder(node, targetId, el, mousePosition);

          if (!indicator) {
            return;
          }
          const newIndicator: CanduCraftIndicator = {
            currentNodeCollectionItemId: el?.dataset.collectionItemId,
            parentNodeCollectionItemId: el?.parentElement?.dataset.collectionItemId,
            draggedElementId: Array.isArray(draggedElement)
              ? (draggedElement as NodeId[])
              : undefined,
            // we end up crashing with weird mutation if we put the node in the store directly
            newRootNode: !Array.isArray(draggedElement) ? cloneDeep(node as Node) : undefined,
            ...indicator,
          };

          store.actions.setNodeEvent(
            EventTypes.draggedOver,
            indicator.placement.parent.id,
            HISTORY_ACTION_MODE.IGNORE,
          );

          store.actions.setIndicator(newIndicator);
          DefaultEventHandlers.indicator = newIndicator;
        };

        const updateIndicator = throttle(
          () => {
            displayIndicator();
          },
          200,
          { leading: true, trailing: false },
        );

        const unbindDragOver = this.addCraftEventListener(el, 'dragover', (e) => {
          e.craft.stopPropagation();
          e.preventDefault();

          const { clientX: x, clientY: y } = e;
          mousePosition = { x, y };

          updateIndicator();
        });

        const unbindDragEnter = this.addCraftEventListener(el, 'dragenter', (e) => {
          e.craft.stopPropagation();
          e.preventDefault();

          const { clientX: x, clientY: y } = e;
          mousePosition = { x, y };

          displayIndicator();
        });

        return () => {
          unbindDragEnter();
          unbindDragOver();
        };
      },

      drag: (el: HTMLElement, id: NodeId) => {
        if (!store.query.node(id).isDraggable()) {
          return () => null;
        }

        const unbindDragStart = this.addCraftEventListener(el, 'dragstart', (e) => {
          e.craft.stopPropagation();
          e.stopPropagation();

          const { query, actions } = store;
          const selectedElementIds = query.getEvent(EventTypes.selected).all();
          const collectionSelectedItemId = selectCollectionItemEvents(this.reduxStore.getState())[
            EventTypes.selected
          ];

          actions.setNodeEvent(EventTypes.dragged, selectedElementIds);

          const selectedDOMs = selectedElementIds.map(
            (nid) => getAllDomNodesFromDocument(nid, collectionSelectedItemId)[0],
          );

          DefaultEventHandlers.draggedElementShadow = createShadow(
            e,
            getDomNodeFromDocument(id, collectionSelectedItemId),
            selectedDOMs,
          ) as HTMLElement;
          DefaultEventHandlers.draggedElement = selectedElementIds;
        });

        const unbindDragEnd = this.addCraftEventListener(el, 'dragend', (e) => {
          e.craft.stopPropagation();
          this.onDropElement();
        });

        return () => {
          el.setAttribute('draggable', 'false');
          unbindDragStart();
          unbindDragEnd();
        };
      },
    } as any;
  }

  private getDropPlaceholder(
    source: NodeSelector,
    target: NodeId,
    targetEl: HTMLElement,
    pos: { x: number; y: number },
  ) {
    const { store } = this.options;
    const { nodes } = store.getState();

    const targetNode = nodes[target];
    if (!targetNode) {
      return null;
    }
    const isTargetCanvas = store.query.node(targetNode.id).isCanvas();

    const targetParent = isTargetCanvas ? targetNode : nodes[targetNode.data.parent];
    const collectionItemId = isTargetCanvas
      ? targetEl.dataset.collectionItemId
      : targetEl.parentElement?.dataset.collectionItemId;

    if (!targetParent) {
      return null;
    }

    const targetParentNodes = targetParent.data.nodes || [];
    const dimensionsInContainer = targetParentNodes.reduce((result, id: NodeId) => {
      const dom = getDomNodeFromDocument(id, collectionItemId);

      if (!dom) {
        return result;
      }
      const info = { id, ...getDOMInfo(dom) };
      return [...result, info];
    }, [] as NodeInfo[]);

    const dropAction = getDropActionFromNodePosition(
      targetParent,
      dimensionsInContainer,
      pos.x,
      pos.y,
    );

    const currentNode =
      targetParentNodes.length > 0 ? nodes[targetParentNodes[dropAction.index]] : targetParent;

    const output: CanduCraftIndicator = {
      placement: {
        ...dropAction,
        currentNode,
      },
      error: false,
    };

    getNodesFromSelector(nodes, source).forEach(({ node: localNode, exists }) => {
      // If source Node is already in the editor, check if it's draggable
      if (exists) {
        store.query.node(localNode.id).isDraggable((err) => {
          output.error = err;
          return err;
        });
      }
    });

    // Check if source Node is droppable in target
    store.query.node(targetParent.id).isDroppable(source, (err) => {
      output.error = err;
      return err;
    });

    return output;
  }

  private onDropElement() {
    const { store } = this.options;

    const { draggedElement, draggedElementShadow, indicator } = DefaultEventHandlers;
    if (draggedElement && indicator && !indicator.error) {
      const { placement, currentNodeCollectionItemId } = indicator as CanduCraftIndicator;
      const index = placement.index + (placement.where === 'after' ? 1 : 0);

      // @ts-expect-error
      store.actions.move(draggedElement, placement.parent.id, index);
      this.reduxStore.dispatch(
        setCollectionItemEvents(EventTypes.selected, currentNodeCollectionItemId),
      );
    }

    if (draggedElementShadow) {
      draggedElementShadow?.parentNode?.removeChild(draggedElementShadow);
      // @ts-expect-error
      DefaultEventHandlers.draggedElementShadow = null;
    }

    // @ts-expect-error
    DefaultEventHandlers.draggedElement = null;
    // @ts-expect-error
    DefaultEventHandlers.indicator = null;

    store.actions.setIndicator(null);
    store.actions.setNodeEvent(EventTypes.draggedOver, [], HISTORY_ACTION_MODE.IGNORE);
    store.actions.setNodeEvent(EventTypes.dragged, []);
  }
}
